import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { releaseService } from "../services/release.service";

export const createRelease = createAsyncThunk("createRelease", async (data, { rejectWithValue }) => {
    try {
        return await releaseService.createRelease(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getReleaseList = createAsyncThunk("getReleaseList", async (params, { rejectWithValue }) => {
    try {
        return await releaseService.getReleaseList(params);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getRelease = createAsyncThunk("getRelease", async (id, { rejectWithValue }) => {
    try {
        return await releaseService.getRelease(id);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const updateRelease = createAsyncThunk("updateRelease", async ({id, data}, { rejectWithValue }) => {
    try {
        return await releaseService.updateRelease(id, data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const deleteRelease = createAsyncThunk("deleteRelease", async (id, { rejectWithValue }) => {
    try {
        return await releaseService.deleteRelease(id);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const addTasksToRelease = createAsyncThunk("addTasksToRelease", async (data, { rejectWithValue }) => {
    try {
        return await releaseService.addTasksToRelease(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const removeTasksFromRelease = createAsyncThunk("removeTasksFromRelease", async (data, { rejectWithValue }) => {
    try {
        return await releaseService.removeTasksFromRelease(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

const releaseSlice = createSlice({
    name: "pmo-release",
    initialState: {
        list: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        detail: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        create: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        update: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        delete: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        addTasks: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        removeTasks: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        }
    },
    reducers: {
        resetStateRelease: (state) => {
            state.list.isError = false;
            state.list.message = null;
            state.detail.isError = false;
            state.detail.message = null;
            state.create.isError = false;
            state.create.message = null;
            state.update.isError = false;
            state.update.message = null;
            state.delete.isError = false;
            state.delete.message = null;
            state.addTasks.isError = false;
            state.addTasks.message = null;
            state.removeTasks.isError = false;
            state.removeTasks.message = null;
        },
    },
    extraReducers: (builder) => {
        // get release list
        builder.addCase(getReleaseList.pending, (state) => {
            state.list.isLoading = true;
        });
        builder.addCase(getReleaseList.fulfilled, (state, action) => {
            state.list.isLoading = false;
            state.list.data = action.payload;
            state.list.isError = false;
        });
        builder.addCase(getReleaseList.rejected, (state, action) => {
            state.list.isLoading = false;
            state.list.isError = true;
            state.list.message = action.payload?.message;
        });

        // get release detail
        builder.addCase(getRelease.pending, (state) => {
            state.detail.isLoading = true;
        });
        builder.addCase(getRelease.fulfilled, (state, action) => {
            state.detail.isLoading = false;
            state.detail.data = action.payload;
            state.detail.isError = false;
        });
        builder.addCase(getRelease.rejected, (state, action) => {
            state.detail.isLoading = false;
            state.detail.isError = true;
            state.detail.message = action.payload?.message;
        });

        // create release
        builder.addCase(createRelease.pending, (state) => {
            state.create.isLoading = true;
        });
        builder.addCase(createRelease.fulfilled, (state, action) => {
            state.create.isLoading = false;
            state.create.data = action.payload;
            state.create.isError = false;
        });
        builder.addCase(createRelease.rejected, (state, action) => {
            state.create.isLoading = false;
            state.create.isError = true;
            state.create.message = action.payload?.message;
        });

        // update release
        builder.addCase(updateRelease.pending, (state) => {
            state.update.isLoading = true;
        });
        builder.addCase(updateRelease.fulfilled, (state, action) => {
            state.update.isLoading = false;
            state.update.data = action.payload;
            state.update.isError = false;
        });
        builder.addCase(updateRelease.rejected, (state, action) => {
            state.update.isLoading = false;
            state.update.isError = true;
            state.update.message = action.payload?.message;
        });

        // delete release
        builder.addCase(deleteRelease.pending, (state) => {
            state.delete.isLoading = true;
        });
        builder.addCase(deleteRelease.fulfilled, (state, action) => {
            state.delete.isLoading = false;
            state.delete.data = action.payload;
            state.delete.isError = false;
        });
        builder.addCase(deleteRelease.rejected, (state, action) => {
            state.delete.isLoading = false;
            state.delete.isError = true;
            state.delete.message = action.payload?.message;
        });

        // add tasks to release
        builder.addCase(addTasksToRelease.pending, (state) => {
            state.addTasks.isLoading = true;
        });
        builder.addCase(addTasksToRelease.fulfilled, (state, action) => {
            state.addTasks.isLoading = false;
            state.addTasks.data = action.payload;
            state.addTasks.isError = false;
        });
        builder.addCase(addTasksToRelease.rejected, (state, action) => {
            state.addTasks.isLoading = false;
            state.addTasks.isError = true;
            state.addTasks.message = action.payload?.message;
        });

        // remove tasks from release
        builder.addCase(removeTasksFromRelease.pending, (state) => {
            state.removeTasks.isLoading = true;
        });
        builder.addCase(removeTasksFromRelease.fulfilled, (state, action) => {
            state.removeTasks.isLoading = false;
            state.removeTasks.data = action.payload;
            state.removeTasks.isError = false;
        });
        builder.addCase(removeTasksFromRelease.rejected, (state, action) => {
            state.removeTasks.isLoading = false;
            state.removeTasks.isError = true;
            state.removeTasks.message = action.payload?.message;
        });
    },
});

export const { resetStateRelease } = releaseSlice.actions;
export default releaseSlice.reducer;
